import axios from 'axios';

/**
 * Axios instance for uHelp API calls.
 * Base URL is picked from the environment variable or defaults to relative (proxied).
 * The Authorization header and X-Tenant-ID are managed by AuthContext after login.
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor ────────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    // Attach stored token on every request (in case headers were reset)
    const token = localStorage.getItem('uhelp_access_token');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor — global error handling ───────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Attempt silent token refresh on 401
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('uhelp_refresh_token');
      if (refreshToken) {
        originalRequest._retry = true;
        try {
          const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL || ''}/api/auth/refresh`,
            { refreshToken },
            { headers: { 'X-Tenant-ID': api.defaults.headers.common['X-Tenant-ID'] } }
          );
          if (data.success) {
            localStorage.setItem('uhelp_access_token', data.accessToken);
            localStorage.setItem('uhelp_refresh_token', data.refreshToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
            originalRequest.headers['Authorization'] = `Bearer ${data.accessToken}`;
            return api(originalRequest);
          }
        } catch {
          // Refresh failed — clear session and redirect to login
          localStorage.removeItem('uhelp_access_token');
          localStorage.removeItem('uhelp_refresh_token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export { api };
export default api;
